import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { SUBSCRIPTION_PLANS } from '../types';
import { personalMarketplaceApi } from '../services/api';

const CATEGORIES = [
  'Electronics',
  'Fashion & Clothing',
  'Home & Garden',
  'Sports & Outdoors',
  'Vehicles',
  'Collectibles',
  'Books & Media',
  'Toys & Games',
  'Arts & Crafts',
  'Services',
  'Other'
];

const CONDITIONS = [
  { value: 'new', label: 'New - Never used, in original packaging' },
  { value: 'like_new', label: 'Like New - Used once or twice, looks new' },
  { value: 'good', label: 'Good - Used but well maintained' },
  { value: 'fair', label: 'Fair - Some wear and tear' },
  { value: 'poor', label: 'Poor - Heavily used, needs repair' },
];

const CreateListing: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [images, setImages] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    condition: '',
    price: '',
    acceptMangoCredits: true,
    location: '',
    shippingAvailable: false,
    pickupOnly: true,
    meetupSafe: false,
  });

  const currentPlan = user ? SUBSCRIPTION_PLANS[user.subscription] : SUBSCRIPTION_PLANS.free;
  const maxListings = currentPlan.limits.productListings;

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    // Demo: convert to data URLs
    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result && images.length < 6) {
          setImages([...images, event.target.result as string]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index: number) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.price || !formData.category || !formData.condition) {
      setError('Please fill in all required fields');
      return;
    }
    if (images.length === 0) {
      setError('Please add at least one image');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await personalMarketplaceApi.create({
        title: formData.title,
        description: formData.description || undefined,
        price: parseFloat(formData.price),
        images,
        category: formData.category || 'Other',
        condition: (formData.condition as 'new' | 'like_new' | 'good' | 'fair' | 'poor') || 'good',
        location: formData.location || undefined,
        acceptMangoCredits: formData.acceptMangoCredits,
        shippingAvailable: formData.shippingAvailable,
        pickupOnly: formData.pickupOnly,
      });
      if (res?.success) {
        navigate('/marketplace/my-listings');
      } else {
        setError(res?.error || 'Failed to create listing');
      }
    } catch (err) {
      console.error('Create listing failed:', err);
      setError('Failed to create listing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ margin: 0, fontSize: '28px', color: '#333' }}>📝 Create Listing</h1>
        <p style={{ margin: '10px 0 0 0', color: '#666' }}>
          List an item for sale on the Mango Marketplace
        </p>
        {maxListings !== -1 && (
          <p style={{ 
            margin: '15px 0 0 0', 
            padding: '10px 15px',
            background: '#fff3e0',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#e65100'
          }}>
            📋 Your {currentPlan.name} plan allows up to {maxListings} active listings.
            <button 
              onClick={() => navigate('/upgrade')}
              style={{
                marginLeft: '10px',
                background: 'none',
                border: 'none',
                color: '#ff6b35',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: '13px'
              }}
            >
              Upgrade for more
            </button>
          </p>
        )}
      </div>

      {error && (
        <div style={{
          background: '#fee',
          color: '#c00',
          padding: '12px 16px',
          borderRadius: '10px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Images */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '25px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <h2 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#333' }}>📷 Photos</h2>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', 
            gap: '15px' 
          }}>
            {images.map((img, index) => (
              <div
                key={index}
                style={{
                  position: 'relative',
                  paddingTop: '100%',
                  borderRadius: '12px',
                  background: `url(${img}) center/cover`,
                  border: index === 0 ? '3px solid #ff6b35' : '1px solid #e0e0e0'
                }}
              >
                {index === 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '-10px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#ff6b35',
                    color: 'white',
                    padding: '3px 10px',
                    borderRadius: '10px',
                    fontSize: '10px',
                    fontWeight: '600'
                  }}>
                    Main Photo
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  style={{
                    position: 'absolute',
                    top: '5px',
                    right: '5px',
                    width: '24px',
                    height: '24px',
                    background: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  ×
                </button>
              </div>
            ))}
            
            {images.length < 6 && (
              <label style={{
                paddingTop: '100%',
                borderRadius: '12px',
                border: '2px dashed #ddd',
                cursor: 'pointer',
                position: 'relative',
                display: 'block'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center'
                }}>
                  <span style={{ fontSize: '28px' }}>+</span>
                  <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#888' }}>Add Photo</p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
              </label>
            )}
          </div>
          <p style={{ margin: '15px 0 0 0', fontSize: '12px', color: '#888' }}>
            Add up to 6 photos. The first photo will be the main image.
          </p>
        </div>

        {/* Details */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '25px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <h2 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#333' }}>📝 Item Details</h2>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#333' }}>
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="What are you selling?"
              maxLength={80}
              required
              style={{
                width: '100%',
                padding: '14px',
                border: '1px solid #e0e0e0',
                borderRadius: '10px',
                fontSize: '15px'
              }}
            />
            <p style={{ margin: '5px 0 0 0', fontSize: '11px', color: '#888', textAlign: 'right' }}>
              {formData.title.length}/80
            </p>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#333' }}>
              Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe your item in detail. Include condition, size, features, etc."
              rows={6}
              maxLength={2000}
              required
              style={{
                width: '100%',
                padding: '14px',
                border: '1px solid #e0e0e0',
                borderRadius: '10px',
                fontSize: '15px',
                resize: 'vertical'
              }}
            />
            <p style={{ margin: '5px 0 0 0', fontSize: '11px', color: '#888', textAlign: 'right' }}>
              {formData.description.length}/2000
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#333' }}>
                Category *
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '14px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '10px',
                  fontSize: '15px',
                  cursor: 'pointer'
                }}
              >
                <option value="">Select category</option>
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#333' }}>
                Condition *
              </label>
              <select
                value={formData.condition}
                onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '14px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '10px',
                  fontSize: '15px',
                  cursor: 'pointer'
                }}
              >
                <option value="">Select condition</option>
                {CONDITIONS.map(cond => (
                  <option key={cond.value} value={cond.value}>{cond.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '25px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <h2 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#333' }}>💰 Pricing</h2>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#333' }}>
              Price (AUD) *
            </label>
            <div style={{ position: 'relative' }}>
              <span style={{
                position: 'absolute',
                left: '14px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '18px',
                color: '#666'
              }}>
                $
              </span>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="0.00"
                min="0"
                step="0.01"
                required
                style={{
                  width: '100%',
                  padding: '14px 14px 14px 35px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '10px',
                  fontSize: '20px',
                  fontWeight: '600'
                }}
              />
            </div>
          </div>

          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            padding: '15px',
            background: formData.acceptMangoCredits ? '#fff3e020' : '#f8f9fa',
            borderRadius: '10px',
            border: formData.acceptMangoCredits ? '1px solid #ff6b3530' : '1px solid #e0e0e0'
          }}>
            <input
              type="checkbox"
              checked={formData.acceptMangoCredits}
              onChange={(e) => setFormData({ ...formData, acceptMangoCredits: e.target.checked })}
              style={{ width: '20px', height: '20px', cursor: 'pointer' }}
            />
            <div>
              <p style={{ margin: 0, fontWeight: '600', color: '#333', fontSize: '14px' }}>
                🥭 Accept Mango Credits
              </p>
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
                Let buyers pay with Mango Credits (1 credit = $1 AUD)
              </p>
            </div>
          </div>
        </div>

        {/* Location & Delivery */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '25px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <h2 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#333' }}>📍 Location & Delivery</h2>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#333' }}>
              Location *
            </label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="e.g., Brisbane, QLD"
              required
              style={{
                width: '100%',
                padding: '14px',
                border: '1px solid #e0e0e0',
                borderRadius: '10px',
                fontSize: '15px'
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[
              { key: 'pickupOnly', label: 'Pickup only', desc: 'Buyer picks up from your location' },
              { key: 'shippingAvailable', label: 'Shipping available', desc: 'You can ship to the buyer' },
              { key: 'meetupSafe', label: 'Safe meetup spot', desc: 'Meet at a safe public location' },
            ].map((option) => (
              <label
                key={option.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  background: '#f8f9fa',
                  borderRadius: '10px',
                  cursor: 'pointer'
                }}
              >
                <input
                  type="checkbox"
                  checked={(formData as any)[option.key]}
                  onChange={(e) => setFormData({ ...formData, [option.key]: e.target.checked })}
                  style={{ width: '18px', height: '18px' }}
                />
                <div>
                  <p style={{ margin: 0, fontSize: '14px', color: '#333' }}>{option.label}</p>
                  <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: '#888' }}>{option.desc}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Submit */}
        <div style={{ display: 'flex', gap: '15px' }}>
          <button
            type="button"
            onClick={() => navigate('/marketplace')}
            style={{
              flex: 1,
              padding: '16px',
              background: '#f0f0f0',
              color: '#333',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              flex: 2,
              padding: '16px',
              background: loading ? '#ccc' : 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Creating listing...' : '🚀 Publish Listing'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateListing;
